import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { useParams } from 'react-router-dom'
import { Pause, Play, SkipBack, SkipForward, Volume2Icon, VolumeXIcon, Settings } from 'lucide-react'
import invariant from 'tiny-invariant'

import { useSafeMutation } from '~/common/use-safe-mutation'
import { CommandAction } from '~/__generated__/graphql'

import { Label } from '~/ui/label'
import { Slider } from '~/ui/slider'
import { useEffect, useRef, useState } from 'react'
import { useZoneStatus } from '~/zones/hooks/use-zone-status'
import { usePlanPermissions } from '~/billing/hooks/use-plan-permissions'
import { useNavigate } from 'react-router-dom'
import { CreditCard } from 'lucide-react'
import { Button } from '~/ui/button'
import { zoneBillingPath } from '~/common/paths'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/ui/dialog'
import { Form } from '~/ui/form'
import { TextField } from '~/fields/text-field'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from '~/hooks/use-toast'
import logoPlaceholder from '~/images/logo-placeholder.svg'

const Skeleton = ({ className }: { className?: string }) => (
  <div className={`animate-pulse rounded bg-gray-200 ${className}`} />
)

export const sendZoneCommandMutation = gql(`
  mutation SendZoneCommand($input: SendZoneCommandInput!) {
    sendZoneCommand(input: $input) {
      status
    }
  }
`)

const GET_ZONE = gql(`
  query GetZone($zoneId: ID!) {
    zone(id: $zoneId) {
      id
      name
      location {
        id
        name
        address
        timeZone
      }
    }
  }
`)

const GET_ZONE_STATUS = gql(`
  query GetZoneStatus($zoneId: ID!) {
    zoneStatus(zoneId: $zoneId) {
      currentTrack {
        id
        name
        artists {
          name
        }
      }
      playing
      currentPosition
      trackDuration
      formattedPosition
      formattedDuration
      volume
      crossfade
    }
  }
`)


const GET_ZONE_PLAYLIST = gql(`
  query GetZonePlaylist($zoneId: ID!) {
    playlistSchedules(zoneId: $zoneId, first: 1) {
      edges {
        node {
          id
          playlist {
            id
            name
            playlistType
            playlistTracks {
              id
              position
              track {
                id
                name
                artists {
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`)

const UPDATE_ZONE_MUTATION = gql(`
  mutation UpdateZone($input: UpdateZoneInput!) {
    updateZone(input: $input) {
      zone {
        id
        name
      }
      errors
    }
  }
`)

export const ZoneScreen = () => {
  const { zoneId } = useParams()
  invariant(zoneId)
  const navigate = useNavigate()
  const [sendZoneCommand] = useSafeMutation(sendZoneCommandMutation)
  const [updateZone] = useSafeMutation(UPDATE_ZONE_MUTATION)
  const { hasRestriction } = usePlanPermissions(zoneId)
  const [settingsOpen, setSettingsOpen] = useState(false)

  // Get initial volume from server instead of hardcoding
  const [userAdjustingVolume, setUserAdjustingVolume] = useState(false)
  const [localVolume, setLocalVolume] = useState<number | null>(null)

  const serverVolumeRef = useRef<number | null>(null)
  const recentlySetVolumeRef = useRef<boolean>(false)
  const recentVolumeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const { data: zoneData, loading: zoneLoading } = useQuery(GET_ZONE, {
    variables: { zoneId },
  })

  const { data: playlistData } = useQuery(GET_ZONE_PLAYLIST, {
    variables: { zoneId },
  })

  // Use ActionCable for real-time zone status updates
  const { status: zoneStatus } = useZoneStatus(zoneId)

  // Get initial status from GraphQL query for fallback
  const { data: initialStatusData } = useQuery(GET_ZONE_STATUS, {
    variables: { zoneId },
    fetchPolicy: 'cache-first',
    skip: !!zoneStatus, // Skip if we have ActionCable status
  })

  // Use ActionCable status if available, fallback to GraphQL query
  const currentZoneStatus = zoneStatus || initialStatusData?.zoneStatus

  useEffect(() => {
    // Debug what's coming from the server
    if (currentZoneStatus?.volume !== undefined) {
      console.log(
        `Server reported volume: ${currentZoneStatus?.volume}, local: ${localVolume}, recently set: ${recentlySetVolumeRef.current}`
      )

      // Always track latest server value
      serverVolumeRef.current = currentZoneStatus?.volume

      // Only update local UI volume if:
      // 1. User isn't adjusting AND
      // 2. We haven't recently set the volume ourselves AND
      // 3. Either this is first load OR there's a significant difference
      if (
        !userAdjustingVolume &&
        !recentlySetVolumeRef.current &&
        (localVolume === null || Math.abs((localVolume ?? 0) - currentZoneStatus?.volume) > 3)
      ) {
        console.log(`Accepting server volume: ${currentZoneStatus?.volume}`)
        setLocalVolume(currentZoneStatus?.volume)
      }
    }
  }, [currentZoneStatus?.volume, userAdjustingVolume, localVolume])

  const handleAction = async (action: CommandAction) => {
    const result = await sendZoneCommand({
      variables: {
        input: {
          action: action.toUpperCase() as CommandAction,
          zoneId: zoneId,
        },
      },
    })
    if (result.data?.sendZoneCommand.status === 'ok') {
      console.log(`Action ${action} executed successfully`)
    } else {
      console.error(`Failed to execute action ${action}`)
    }
  }

  const handleVolumeChange = async (volumeValue: number) => {
    console.log(`Setting volume to ${volumeValue}`)
    try {
      // Update local UI immediately
      setLocalVolume(volumeValue)

      // Set flag to ignore server updates for a while
      recentlySetVolumeRef.current = true

      // Clear any existing timeout
      if (recentVolumeTimeoutRef.current) {
        clearTimeout(recentVolumeTimeoutRef.current)
      }

      // Set a longer timeout (5 seconds) before accepting server values again
      recentVolumeTimeoutRef.current = setTimeout(() => {
        recentlySetVolumeRef.current = false
      }, 5000)

      const result = await sendZoneCommand({
        variables: {
          input: {
            action: CommandAction.SetVolume,
            zoneId: zoneId,
            arg: volumeValue.toString(),
          },
        },
      })

      if (result.data?.sendZoneCommand.status === 'ok') {
        console.log(`Volume set to ${volumeValue}`)
        // Update our reference without triggering re-renders
        serverVolumeRef.current = volumeValue
      } else {
        console.error(
          `Failed to set volume: ${result.data?.sendZoneCommand.status || 'Unknown error'}`
        )
      }
    } catch (error) {
      console.error('Volume command failed:', error)
    }
  }

  const handleSeek = (absoluteTimeInSeconds: number) => {
    if (currentZoneStatus?.trackDuration && currentZoneStatus?.trackDuration > 0) {
      sendZoneCommand({
        variables: {
          input: {
            zoneId: zoneId,
            action: CommandAction.SetPosition,
            arg: absoluteTimeInSeconds.toString(),
          },
        },
      })
    }
  }

  if (!zoneLoading && (!zoneData || !zoneData?.zone)) {
    return <div>Zone not found</div>
  }

  if (!currentZoneStatus) {
    return <div>Loading...</div>
  }

  if (!currentZoneStatus.currentTrack) {
    const playlist = playlistData?.playlistSchedules?.edges?.[0]?.node?.playlist
    if (!playlist) {
      return (
        <div className="flex h-full w-full flex-1 items-center justify-center bg-gray-50 p-6">
          <div className="w-full max-w-xl rounded-lg border bg-white p-6">
            <h1 className="text-xl font-semibold">
              Zone: {zoneData?.zone?.location?.name} - {zoneData?.zone?.name}
            </h1>
            <p className="text-muted-foreground mt-2">
              This zone doesn’t have a schedule yet. Create a schedule to start playback.
            </p>
            <div className="mt-4 flex gap-2">
              <Button
                className="cursor-pointer"
                onClick={() => navigate(`/schedules/new?zoneId=${zoneId}`)}
              >
                Create schedule
              </Button>
              <Button
                variant="outline"
                className="cursor-pointer"
                onClick={() => navigate(zoneBillingPath({ zoneId }))}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Billing
              </Button>
            </div>
          </div>
        </div>
      )
    }
    const playTrack = async (trackId: string) => {
      await sendZoneCommand({
        variables: {
          input: { zoneId, action: CommandAction.Play, arg: trackId },
        },
      })
    }
    return (
      <div className="flex h-full w-full flex-1">
        <div className="flex flex-1 flex-col">
          <div className="flex items-center justify-between p-3">
            <h1 className="text-2xl">
              Zone: {zoneData?.zone?.location?.name} - {zoneData?.zone?.name}
            </h1>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(zoneBillingPath({ zoneId }))}>
                <CreditCard className="mr-2 h-4 w-4" />
                Billing
              </Button>
            </div>
          </div>
          <hr className="border-0 border-b" />
          <div className="flex min-h-0 flex-1">
            <div className="flex flex-1 items-center justify-center bg-gray-100 p-4">
              <div className="text-muted-foreground">No track is currently playing.</div>
            </div>
            <div className="w-[420px] border-l bg-white p-4">
              <div className="mb-3 font-medium">{playlist.name}</div>
              <div className="space-y-2">
                {playlist.playlistTracks?.map((pt: any) => (
                  <button
                    key={pt.id}
                    className="w-full cursor-pointer rounded-md border p-2 text-left hover:bg-neutral-50"
                    onClick={() => playTrack(pt.track.id)}
                  >
                    <div className="text-sm font-medium">{pt.track.name}</div>
                    <div className="text-muted-foreground text-xs">
                      {pt.track.artists?.map((a: any) => a.name).join(', ') || 'Unknown Artist'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (zoneLoading) {
    return (
      <div className="flex h-full w-full flex-1">
        <div className="flex flex-1 flex-col">
          <div className="flex items-center justify-between p-3">
            <h1 className="text-2xl">
              Zone: {zoneData?.zone?.location?.name} - {zoneData?.zone?.name}
            </h1>
          </div>
          <hr className="border-0 border-b" />
          <div className="flex h-full items-center justify-center bg-gray-100 p-4">
            <div className="w-80 rounded-lg bg-white p-8 shadow-md">
              <Skeleton className="mx-auto mb-4 h-64 w-64 rounded-lg" />
              <Skeleton className="mx-auto mb-2 h-6 w-48" />
              <Skeleton className="mx-auto mb-6 h-4 w-32" />
              <div className="mt-6 flex items-center justify-center">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="mx-4 h-12 w-12 rounded-full" />
                <Skeleton className="h-10 w-10 rounded-full" />
              </div>
              <Skeleton className="mt-6 h-2 w-full rounded-full" />
              <div className="mt-2 flex justify-between">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-12" />
              </div>
              <div className="mt-3 space-y-3 rounded-lg bg-gray-100 p-3">
                <div className="flex items-center justify-between gap-2">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-3 w-8" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-2 flex-1 rounded-full" />
                  <Skeleton className="h-4 w-4 rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-1">
      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-between p-3">
          <h1 className="text-2xl">
            Zone: {zoneData?.zone?.location?.name} - {zoneData?.zone?.name}
          </h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(zoneBillingPath({ zoneId }))}>
              <CreditCard className="mr-2 h-4 w-4" />
              Billing
            </Button>
          </div>
        </div>
        <hr className="border-0 border-b" />
        <div className="flex min-h-0 flex-1">
          <div className="flex flex-1 items-center justify-center bg-gray-100 p-4">
            <div className="w-80 rounded-lg bg-white p-8 shadow-md">
            <img
              src={currentZoneStatus?.currentTrack?.coverUrl ?? logoPlaceholder}
              alt={currentZoneStatus?.currentTrack?.name}
              className="mx-auto mb-4 h-64 w-64 rounded-lg shadow-lg shadow-teal-50"
            />
            <h2 className="text-center text-xl font-semibold">{currentZoneStatus?.currentTrack?.name}</h2>
            <p className="text-center text-sm text-gray-600">
              {currentZoneStatus?.currentTrack?.artists?.map((a: any) => a.name).join(', ') ||
                'Unknown Artist'}
            </p>
            <div className="mt-6 flex items-center justify-center">
              {!hasRestriction('player_controls.next_previous') && (
                <button
                  className="cursor-pointer rounded-full bg-gray-200 p-3 hover:bg-gray-300 focus:outline-none"
                  onClick={() => handleAction(CommandAction.Prev)}
                >
                  <SkipBack className="h-4 w-4 text-gray-600" />
                </button>
              )}
              <button
                className={`${!hasRestriction('player_controls.next_previous') ? 'mx-4' : ''} cursor-pointer rounded-full bg-gray-200 p-4 hover:bg-gray-300 focus:outline-none`}
                onClick={() =>
                  handleAction(currentZoneStatus?.playing ? CommandAction.Pause : CommandAction.Play)
                }
              >
                {currentZoneStatus?.playing && !(currentZoneStatus as any)?.paused ? (
                  <Pause className="h-7 w-7 text-gray-600" />
                ) : (
                  <Play className="h-7 w-7 text-gray-600" />
                )}
              </button>
              {!hasRestriction('player_controls.next_previous') && (
                <button
                  className="cursor-pointer rounded-full bg-gray-200 p-3 hover:bg-gray-300 focus:outline-none"
                  onClick={() => handleAction(CommandAction.Next)}
                >
                  <SkipForward className="h-4 w-4 text-gray-600" />
                </button>
              )}
            </div>
            {!hasRestriction('player_controls.seek') && (
              <div
                className="mt-6 h-2 cursor-pointer rounded-full bg-gray-200"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  const clickX = e.clientX - rect.left
                  const percentage = clickX / rect.width
                  const newPosition = percentage * (currentZoneStatus?.trackDuration || 0)
                  handleSeek(newPosition)
                }}
              >
                <div
                  className="pointer-events-none h-2 rounded-full bg-teal-500 transition-all duration-300"
                  style={{
                    width: `${currentZoneStatus?.trackDuration && currentZoneStatus?.trackDuration > 0 ? (currentZoneStatus?.currentPosition / currentZoneStatus?.trackDuration) * 100 : 0}%`,
                  }}
                ></div>
              </div>
            )}
            <div className="mt-2 flex justify-between text-sm text-gray-600">
              <span>{currentZoneStatus?.formattedPosition}</span>
              <span>{currentZoneStatus?.formattedDuration}</span>
            </div>
            <div className="mt-3 space-y-3 rounded-lg bg-gray-100 p-3">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-muted-foreground/70 text-xs font-normal">Volume</Label>
                <output className="text-muted-foreground/70 text-xs font-normal tabular-nums">
                  {localVolume ?? 0}
                  {'%'}
                  {/* {serverVolumeRef.current !== localVolume
                    ? `(Server: ${serverVolumeRef.current})`
                    : ''} */}
                </output>
              </div>
              <div className="flex items-center gap-2">
                <VolumeXIcon className="shrink-0 opacity-60" size={16} aria-hidden="true" />
                <Slider
                  value={[localVolume ?? serverVolumeRef.current ?? 70]}
                  min={0}
                  max={100}
                  step={1}
                  onValueChange={(value: number[]) => {
                    if (userAdjustingVolume) {
                      setLocalVolume(value[0])
                    }
                  }}
                  onValueCommit={(value: number[]) => {
                    handleVolumeChange(value[0])

                    // Delay resetting the flag to prevent UI jumps
                    setTimeout(() => {
                      setUserAdjustingVolume(false)
                    }, 500) // Longer delay to ensure server has time to respond
                  }}
                  onPointerDown={() => {
                    setUserAdjustingVolume(true)
                  }}
                  aria-label="Volume slider"
                />
                <Volume2Icon className="shrink-0 opacity-60" size={16} aria-hidden="true" />
              </div>
            </div>
          </div>
          </div>

          <div className="w-[420px] overflow-y-auto border-l bg-white p-4">
            {(() => {
              const playlist = playlistData?.playlistSchedules?.edges?.[0]?.node?.playlist
              if (!playlist) {
                return (
                  <div className="text-muted-foreground">
                    No playlist schedule found for this zone.
                  </div>
                )
              }
              return (
                <>
                  <div className="mb-3 font-medium">{playlist.name}</div>
                  <div className="space-y-2">
                    {playlist.playlistTracks?.map((pt: any) => (
                      <div key={pt.id} className="rounded-md border p-2">
                        <div className="text-sm font-medium">{pt.track.name}</div>
                        <div className="text-muted-foreground text-xs">
                          {pt.track.artists?.map((a: any) => a.name).join(', ') || 'Unknown Artist'}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )
            })()}
          </div>
        </div>
      </div>

      <ZoneSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        zone={zoneData?.zone}
        onSave={async (newName: string) => {
          const res = await updateZone({ variables: { input: { id: zoneId, name: newName } } })
          if (res.data?.updateZone?.errors?.length) {
            toast({
              title: 'Save failed',
              description: res.data.updateZone.errors.join(', '),
              variant: 'destructive',
            })
            return
          }
          toast({ title: 'Zone updated' })
        }}
      />
    </div>
  )
}

const zoneSettingsSchema = z.object({
  name: z.string().min(1),
})

function ZoneSettingsDialog({
  open,
  onOpenChange,
  zone,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  zone: any
  onSave: (name: string) => Promise<void>
}) {
  const form = useForm<{ name: string }>({
    resolver: zodResolver(zoneSettingsSchema),
    defaultValues: { name: zone?.name ?? '' },
    values: { name: zone?.name ?? '' },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Zone settings</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit(async (values) => {
              await onSave(values.name)
              onOpenChange(false)
            })}
          >
            <TextField control={form.control} name="name" label="Zone name" required />
            <DialogFooter>
              <Button type="button" variant="link" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" className="cursor-pointer">
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
